/* eslint-disable react/prop-types */
import { Button } from "@/components/ui/button";
import { Trash2, PenLine, Archive, ArchiveRestore } from "lucide-react";
import { noImg } from "@/assets";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import vodka from "@/components/icons/menu-management/vodka.svg";
import { calculatePriceWithVAT } from "@/lib/utils";
import moment from "moment";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Link } from "react-router-dom";
import QRCode from "react-qr-code";

export function DeleteConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  icon = <Trash2 />,
  title = "Confirm Delete",
  yesBtnTxt = "Yes, Delete",
  noBtnTxt = "Cancel",
  yesBtnClass = "bg-cred",
  description = "You want to delete all the marked item, this can’t be undone once you delete.",
  isLoading = false,
  autoCloseOnConfirm = true,
  children,
}) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader className={`flex justify-center items-center gap-2`}>
          <Button className="bg-cblack7" size="icon" variant="">
            {/* <Trash2 className="text-cred" /> */}
            <span className="text-cred">{icon}</span>
          </Button>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
          {children}
        </DialogHeader>
        <DialogFooter>
          <div className={`flex w-full justify-center gap-5 items-center`}>
            <Button className="bg-cblack7 text-white" onClick={onClose}>
              {noBtnTxt || "No"}
            </Button>
            <Button
              isLoading={isLoading}
              variant="destructive"
              className={yesBtnClass}
              onClick={() => {
                onConfirm();
                if (autoCloseOnConfirm) {
                  onClose();
                }
              }}
            >
              {yesBtnTxt || "Yes"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ArchiveConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  isArchive = true,
  isLoading = false,
}) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px] bg-[#121212] border border-neutral-800 text-white rounded-2xl overflow-hidden shadow-2xl p-6">
        <DialogHeader className="flex flex-col items-center gap-4 text-center">
          <div className={`p-4 rounded-full ${isArchive ? 'bg-amber-500/10 text-amber-500' : 'bg-cgreen/10 text-cgreen'} animate-bounce`}>
            {isArchive ? <Archive size={32} /> : <ArchiveRestore size={32} />}
          </div>
          <div className="space-y-1">
            <DialogTitle className="text-xl font-semibold tracking-tight text-white">
              {title}
            </DialogTitle>
            <DialogDescription className="text-sm text-neutral-400">
              {description}
            </DialogDescription>
          </div>
        </DialogHeader>
        <DialogFooter className="flex w-full justify-center gap-4 mt-6">
          <Button 
            className="flex-1 bg-neutral-900 border border-neutral-800 text-neutral-300 hover:bg-neutral-800 hover:text-white transition-colors"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            isLoading={isLoading}
            className={`flex-1 font-semibold ${isArchive ? 'bg-amber-500 text-black hover:bg-amber-400' : 'bg-cgreen text-white hover:bg-cgreen/90'} transition-all`}
            onClick={() => {
              onConfirm();
              onClose();
            }}
          >
            {isArchive ? "Yes, Archive" : "Yes, Restore"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function VariantDialog({
  crust = false,
  isOpen,
  onClose,
  items,
  link,
  teaCoffee = false,
}) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[1000px]">
        <DialogTitle></DialogTitle>
        <div>
          <h1 className="text-cwhite2 absolute top-3 left-3 capitalize">
            {items?.name} Variants
          </h1>
        </div>
        <div className="mt-5">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>Calorie Amount</TableHead>
                {crust && <TableHead>Crust Type</TableHead>}
                <TableHead>{teaCoffee ? "size" : "Variant"}</TableHead>
                <TableHead>In-Price(exc VAT)</TableHead>
                <TableHead>In-Price(inc VAT)</TableHead>
                <TableHead>Out-Price(exc VAT)</TableHead>
                <TableHead>Out-Price(inc VAT)</TableHead>
                {/* <TableHead className="text-right">Actions</TableHead> */}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items?.variants?.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">
                    {item?.calorie_amount || "0"} kcal
                  </TableCell>
                  {crust && <TableCell>{item?.crust_name || "N/A"}</TableCell>}
                  <TableCell>{item?.name || "N/A"}</TableCell>

                  <TableCell>${item?.in_price || "00"}</TableCell>
                  <TableCell>
                    ${calculatePriceWithVAT(item?.in_price, items?.vat) || "0"}
                  </TableCell>
                  <TableCell>${item?.out_price || "0"}</TableCell>
                  <TableCell>
                    ${calculatePriceWithVAT(item?.out_price, items?.vat) || "0"}
                  </TableCell>
                  {/* <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Link to={link || "/backoffice/menu-management"}>
                        <PenLine
                          size={18}
                          className="cursor-pointer text-cyellow"
                        />
                      </Link>

                      <div onClick={() => openDialog(item.id, item.name)}>
                        <Trash2
                          size={18}
                          className="cursor-pointer text-cred "
                        />
                      </div>
                    </div>
                  </TableCell> */}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function IngredientsDialog({ isOpen, onClose, items }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogTitle></DialogTitle>
        <div>
          <h1 className="text-cwhite2 absolute top-3 left-3 capitalize">
            {items?.name} Ingredients
          </h1>
        </div>
        <div className="mt-5">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                {/* <TableHead className="text-right">Actions</TableHead> */}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items?.ingredients?.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">
                    {item?.name || "No name"}
                  </TableCell>
                  <TableCell>{item?.description || "No description"}</TableCell>
                  {/* <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <div onClick={() => openDialog(item?.id, item?.name)}>
                        <Trash2
                          size={18}
                          className="cursor-pointer text-cred "
                        />
                      </div>
                    </div>
                  </TableCell> */}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AddOns({ isOpen, onClose, items, title }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogTitle></DialogTitle>
        <div>
          <h1 className="text-cwhite2 absolute top-3 left-3 capitalize">
            {items?.name} Add Ons
          </h1>
        </div>
        <div className="mt-5">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Price</TableHead>
                {/* <TableHead className="text-right">Actions</TableHead> */}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items?.add_ons?.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">
                    {item?.name || "No name"}
                  </TableCell>
                  <TableCell>{item?.description || "No description"}</TableCell>
                  <TableCell>${item?.price || "0"}</TableCell>
                  {/* <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <div onClick={() => openDialog(item?.id, item?.name)}>
                        <Trash2
                          size={18}
                          className="cursor-pointer text-cred "
                        />
                      </div>
                    </div>
                  </TableCell> */}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}
export function ReservationTableItems({ isOpen, onClose, items, title }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogTitle></DialogTitle>
        <div>
          <h1 className="text-cwhite2 absolute top-3 left-3 capitalize">
            Tables List
          </h1>
        </div>
        <div className="mt-5">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Seats</TableHead>
                {/* <TableHead>Price</TableHead> */}
                {/* <TableHead className="text-right">Actions</TableHead> */}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items?.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">
                    {item?.name || "No name"}
                  </TableCell>
                  <TableCell>{item?.seat || "No Seat"}</TableCell>
                  {/* <TableCell>${item?.price || "0"}</TableCell> */}
                  {/* <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <div onClick={() => openDialog(item?.id, item?.name)}>
                        <Trash2
                          size={18}
                          className="cursor-pointer text-cred "
                        />
                      </div>
                    </div>
                  </TableCell> */}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AllergenDialog({ isOpen, onClose, items, title }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogTitle></DialogTitle>
        <div>
          <h1 className="text-cwhite2 absolute top-3 left-3 capitalize">
            {items?.name} Allergen
          </h1>
        </div>
        <div className="mt-5">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Allergen Key</TableHead>
                {/* <TableHead className="text-right">Actions</TableHead> */}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items?.allergens?.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">{item?.name}</TableCell>
                  <TableCell>{item?.short_name || "N/A"}</TableCell>
                  {/* <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <div onClick={() => openDialog(item.id, item.name)}>
                        <Trash2
                          size={18}
                          className="cursor-pointer text-cred "
                        />
                      </div>
                    </div>
                  </TableCell> */}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DetailsDialog({ isOpen, onClose, items }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogTitle></DialogTitle>
        <div>
          <h1 className="text-cwhite2 absolute top-3 left-3 capitalize">
            {items?.name || items?.name} Details
          </h1>
        </div>
        <div className="mt-5">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>Description</TableHead>
                {(items?.category_name || items?.category?.name) && (
                  <TableHead>Category</TableHead>
                )}
                {items?.brand_name && <TableHead>Brand</TableHead>}
                <TableHead>VAT</TableHead>
                {(items?.alcohol_percentage !== undefined && items?.alcohol_percentage !== null) && (
                  <TableHead>Alcohol (%)</TableHead>
                )}
                {(items?.calorie !== undefined && items?.calorie !== null) && (
                  <TableHead>Calories</TableHead>
                )}
                {(items?.show_only_25th_december !== undefined && items?.show_only_25th_december !== null) && (
                  <TableHead>Show Only 25th Dec</TableHead>
                )}
                {items?.spice_level && (
                  <TableHead className="text-right">Spice Level</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array(1)
                .fill(null)
                .map((_, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">
                      {items?.description || "No description"}
                    </TableCell>
                    {(items?.category_name || items?.category?.name) && (
                      <TableCell>
                        {items?.category_name || items?.category?.name || "N/A"}
                      </TableCell>
                    )}
                    {items?.brand_name && (
                      <TableCell>{items?.brand_name || "N/A"}</TableCell>
                    )}
                    <TableCell>{items?.vat || "0"}%</TableCell>
                    {(items?.alcohol_percentage !== undefined && items?.alcohol_percentage !== null) && (
                      <TableCell>{items?.alcohol_percentage}%</TableCell>
                    )}
                    {(items?.calorie !== undefined && items?.calorie !== null) && (
                      <TableCell>{items?.calorie} kcal</TableCell>
                    )}
                    {(items?.show_only_25th_december !== undefined && items?.show_only_25th_december !== null) && (
                      <TableCell>
                        {items?.show_only_25th_december ? "Yes" : "No"}
                      </TableCell>
                    )}
                    {items?.spice_level && (
                      <TableCell className="flex w-full gap-3 items-center justify-end">
                        {items?.spice_level === "mild" && <span>Mild</span>}
                        {items?.spice_level === "fairly_hot" && (
                          <>
                            <img src={vodka} alt="" />
                            <img src={vodka} alt="" />
                          </>
                        )}
                        {items?.spice_level === "medium_hot" && (
                          <>
                            <img src={vodka} alt="" />
                            <img src={vodka} alt="" />
                            <img src={vodka} alt="" />
                          </>
                        )}
                        {items?.spice_level === "very_hot" && (
                          <>
                            <img src={vodka} alt="" />
                            <img src={vodka} alt="" />
                            <img src={vodka} alt="" />
                            <img src={vodka} alt="" />
                          </>
                        )}
                        {items?.spice_level === "extremely_hot" && (
                          <>
                            <img src={vodka} alt="" />
                            <img src={vodka} alt="" />
                            <img src={vodka} alt="" />{" "}
                            <img src={vodka} alt="" />{" "}
                            <img src={vodka} alt="" />
                          </>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function BaseDialog({ isOpen, onClose, items }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogTitle></DialogTitle>
        <div>
          <h1 className="text-cwhite2 absolute top-3 left-3 capitalize">
            {items?.name} Base
          </h1>
        </div>
        <div className="mt-5">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Price</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array(1)
                .fill(null)
                .map((_, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">
                      {items?.base?.name || "N/A"}
                    </TableCell>
                    <TableCell>{items?.base?.description || "N/A"}</TableCell>
                    <TableCell>{items?.base?.price || "0"}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ToppingsDialog({ isOpen, onClose, items }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogTitle></DialogTitle>
        <div>
          <h1 className="text-cwhite2 absolute top-3 left-3 capitalize">
            {items?.name} Toppings
          </h1>
        </div>
        <div className="mt-5">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Price</TableHead>
                {/* <TableHead className="text-right">Actions</TableHead> */}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items?.toppings?.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell>{item.price}</TableCell>
                  {/* <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <div onClick={() => openDialog(item.id, item.name)}>
                        <Trash2
                          size={18}
                          className="cursor-pointer text-cred "
                        />
                      </div>
                    </div>
                  </TableCell> */}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SaladDialog({ isOpen, onClose, items }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogTitle></DialogTitle>
        <div>
          <h1 className="text-cwhite2 absolute top-3 left-3 capitalize">
            {items?.name} Salad
          </h1>
        </div>
        <div className="mt-5">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Price</TableHead>
                {/* <TableHead className="text-right">Actions</TableHead> */}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items?.salads?.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell>{item.price}</TableCell>
                  {/* <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <div onClick={() => openDialog(item.id, item.name)}>
                        <Trash2
                          size={18}
                          className="cursor-pointer text-cred "
                        />
                      </div>
                    </div>
                  </TableCell> */}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SauceDialog({ isOpen, onClose, items }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogTitle></DialogTitle>
        <div>
          <h1 className="text-cwhite2 absolute top-3 left-3 capitalize">
            {items?.name} Sauce
          </h1>
        </div>
        <div className="mt-5">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Price</TableHead>
                {/* <TableHead className="text-right">Actions</TableHead> */}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items?.sauces?.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell>{item.description}</TableCell>
                  <TableCell>{item.price}</TableCell>
                  {/* <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <div onClick={() => openDialog(item.id, item.name)}>
                        <Trash2
                          size={18}
                          className="cursor-pointer text-cred "
                        />
                      </div>
                    </div>
                  </TableCell> */}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ItemsDialog({ isOpen, onClose, items }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogTitle></DialogTitle>
        <div>
          <h1 className="text-cwhite2 absolute top-3 left-3 capitalize">
            {items?.name} Item Details
          </h1>
        </div>
        <div className="mt-5">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Quantity</TableHead>
                {/* <TableHead className="text-right">Actions</TableHead> */}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items?.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">
                    {item?.menu_item?.name}
                    {item?.variant?.name && ` (${item.variant.name})`}
                  </TableCell>
                  <TableCell>{item.quantity || "0"}</TableCell>
                  {/* <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <div onClick={() => openDialog(item.id, item.name)}>
                        <Trash2
                          size={18}
                          className="cursor-pointer text-cred "
                        />
                      </div>
                    </div>
                  </TableCell> */}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DayAndDateDialog({ isOpen, onClose, items }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogTitle></DialogTitle>
        <div>
          <h1 className="text-cwhite2 absolute top-3 left-3 capitalize">
            {items?.name} Days
          </h1>
        </div>
        <div className="mt-5">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>Day</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items?.combo_days?.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">
                    {item?.day_name}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ImageDialog({ isOpen, onClose, items }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogTitle></DialogTitle>
        <div>
          <h1 className="text-cwhite2 absolute top-3 left-3 capitalize">
            {items?.name?.length > 20
              ? items.name.slice(0, 20) + "..."
              : items?.name}
          </h1>
        </div>
        <div className="mt-5">
          <div className="w-full ">
            <img
              src={items?.file_manager_url || items?.image?.file_url || noImg}
              alt={items?.name}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
export function QRDialog({ isOpen, onClose, items }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogTitle></DialogTitle>
        <div>
          <h1 className="text-cwhite2 absolute top-3 left-3 capitalize">
            {items?.name?.length > 20
              ? items?.name.slice(0, 20) + "..."
              : items?.name}
          </h1>
        </div>
        <div className="mt-5">
          <div className="w-full flex justify-center">
            <QRCode
              size={256}
              style={{ height: "auto", width: "250px", padding: '16px', background: 'white' }}
              value={items?.url}
              viewBox={`0 0 256 256`}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CourseItemDialog({ isOpen, onClose, items }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px]">
        <DialogTitle></DialogTitle>
        <div>
          <h1 className="text-cwhite2 absolute top-3 left-3 capitalize">
            Course item Details
          </h1>
        </div>
        <div className="mt-5">
          <Table className="border border-gray-700 rounded-2xl">
            <TableHeader className="">
              <TableRow>
                <TableHead className="w-[200px] text-white text-base">
                  Course Name
                </TableHead>
                <TableHead className="text-white text-base">
                  Item Name
                </TableHead>
                <TableHead className="text-white text-base text-center">
                  Price
                </TableHead>
                {/* <TableHead className="text-white text-base text-center">
                  Action
                </TableHead> */}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items?.map((section, sectionIndex) =>
                section?.course_items?.map((item, itemIndex) => (
                  <TableRow
                    key={`${sectionIndex}-${itemIndex}`}
                    className="hover:bg-neutral-800"
                  >
                    {itemIndex === 0 && (
                      <TableCell
                        rowSpan={section?.course_items?.length}
                        className="align-top text-white text-base border-r border-neutral-700"
                      >
                        <div className="flex flex-col justify-center items-center gap-1">
                          <span className="font-semibold">
                            {section?.name || "No name"}
                          </span>
                          <span className="text-sm whitespace-pre-line text-center">
                            {section?.description || "No description"}
                          </span>
                        </div>
                      </TableCell>
                    )}
                    <TableCell className="text-white">
                      {item?.menu_item?.name}
                    </TableCell>
                    <TableCell className="text-white text-center">
                      ${item?.price}
                    </TableCell>
                    {/* <TableCell className="text-center">
                      <Trash2 className="text-red-500 cursor-pointer hover:text-red-600" />
                    </TableCell> */}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ExtraChargeDialog({ isOpen, onClose, items }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogTitle></DialogTitle>
        <div>
          <h1 className="text-cwhite2 absolute top-3 left-3 capitalize">
            Extra Item Charge
          </h1>
        </div>
        <div className="mt-5">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Price</TableHead>
                {/* <TableHead className="text-right">Actions</TableHead> */}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items?.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">
                    {item?.menu_item?.name}
                  </TableCell>
                  <TableCell>{item?.extra_charge}</TableCell>
                  {/* <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <div onClick={() => openDialog(item.id, item.name)}>
                        <Trash2
                          size={18}
                          className="cursor-pointer text-cred "
                        />
                      </div>
                    </div>
                  </TableCell> */}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DaysDialog({ isOpen, onClose, items }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogTitle></DialogTitle>
        <div>
          <h1 className="text-cwhite2 absolute top-3 left-3 capitalize">
            Available Days
          </h1>
        </div>
        <div className="mt-5">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>Day name</TableHead>
                {/* <TableHead className="text-right">Actions</TableHead> */}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items?.days?.map((item, index) => (
                <TableRow key={index}>
                  <TableCell className="font-medium">{item?.name}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DeMergeConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  icon = <Trash2 />,
  title = "Confirm Demerge",
  description = "Are you sure you want to Demerge? Demerging will release the tables and make them free/available.",
  yesBtnTxt = "Yes",
  noBtnTxt = "Cancel",
  isLoading = false,
}) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader className={`flex justify-center items-center gap-2`}>
          <Button className="bg-cblack7" size="icon" variant="">
            {/* <Trash2 className="text-cred" /> */}
            <span className="text-cred">{icon}</span>
          </Button>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="text-center">{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <div className={`flex w-full justify-center gap-5 items-center`}>
            <Button className="bg-cblack7 text-white" onClick={onClose}>
              {noBtnTxt || "No"}
            </Button>
            <Button
              isLoading={isLoading}
              variant="destructive"
              className="bg-cred"
              onClick={() => {
                onConfirm();
                onClose();
              }}
            >
              {yesBtnTxt || "Yes"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FoodItemsDialog({ isOpen, onClose, items }) {
  const itemsForShow = [
    ...(items?.gift_card_items ?? []),
    ...(items?.voucher_items ?? []),
    ...(items?.c2c_gift_card_items ?? []),
    ...(items?.point_base_card_items ?? [])
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogTitle className="capitalize">
          {items?.name || "Items"} Ingredients
        </DialogTitle>

        <div className="mt-4">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>Item Type</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Variant</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {itemsForShow.length > 0 ? (
                itemsForShow.map((item) => (
                  <TableRow key={item?.id || item?.menu_item?.id}>
                    <TableCell className="font-medium capitalize">
                      {item?.item_type?.replace(/_/g, " ") ?? "N/A"}
                    </TableCell>

                    <TableCell className="font-medium">
                      {item?.menu_item?.name ?? "N/A"}
                    </TableCell>

                    <TableCell>
                      {item?.menu_item?.description ?? "N/A"}
                    </TableCell>

                    <TableCell>
                      {item?.variant?.name ?? "N/A"}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted">
                    No items available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CustomerInfoDialog({ isOpen, onClose, items }) {
  // console.log(items);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogTitle></DialogTitle>
        <div>
          <h1 className="text-cwhite2 absolute top-3 left-3 capitalize">
            Customer Information
          </h1>
        </div>
        <div className="mt-5">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Email</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="font-medium">
                  {items?.customer?.full_name || "N/A"}
                </TableCell>
                <TableCell>{items?.customer?.number || "N/A"}</TableCell>
                <TableCell>{items?.customer?.email || "N/A"}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
}